import { ArrowLeft, UserCircle2, CarFront, CheckCircle, Copy, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { BookingDetails } from './BookingDetails';
import { VehiclePreference } from './VehiclePreference';
import { TermsAndConditions } from './TermsAndConditions';
import { ActionButtons } from './ActionButtons';
import { type BookingSummaryProps, type CommunicationPreferences } from './types';
import ProgressDialog from '../ProgressDialog';
import { trackEvent, trackBookingEvent, trackUserInteraction, trackError } from '../../services/analytics';
import { useNavigate } from 'react-router-dom';
import { AnonymousBookingCredentials } from '../AnonymousBookingCredentials';

const formatCommunicationPrefs = (prefs: CommunicationPreferences | undefined): string => {
  if (!prefs) return 'Not specified';
  const selectedMethods = [];
  if (prefs.email) selectedMethods.push('Email');
  if (prefs.sms) selectedMethods.push('SMS');
  if (prefs.voice) selectedMethods.push('Voice Call');
  if (prefs.whatsapp) selectedMethods.push('WhatsApp');
  return selectedMethods.length > 0 ? selectedMethods.join(', ') : 'None selected';
};

export function BookingSummary({
  pickupLocation,
  dropoffLocation,
  phoneNumber,
  countryCode,
  comfortLevel,
  estimatedDuration,
  onBack,
  onConfirm,
  onCancel,
  email,
  bookingId,
  passengerName,
  pickupDate,
  pickupTime,
  passengers,
  luggage,
  companyName,
  pickupType,
  paymentMethod,
  communicationPrefs,
  sendEmailCopy,
  emailCopyAddress,
  isParentSubmitting = false,
  isConfirmButtonLoading = false,
  mode = 'summary', // Default to summary view
  bookingStatus = 'pending', // Default status is pending
  bookingNumber,
  onDone,
  timezone,
  createdAt,
  serviceKey,
  isAnonymousBooking,
  generatedPassword,
}: BookingSummaryProps & { serviceKey: string }) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Add debugging log for when component mounts
  useEffect(() => {
    console.log('BookingSummary: Component mounted with serviceKey:', serviceKey);
    console.log('BookingSummary: Current location:', window.location.pathname);
  }, []);

  // Enhanced logging for serviceKey changes
  useEffect(() => {
    console.log('BookingSummary: serviceKey prop value changed to:', serviceKey);
    console.log('BookingSummary: serviceKey type:', typeof serviceKey);
    if (!serviceKey) {
      console.error('BookingSummary: serviceKey is undefined or empty');
    }
  }, [serviceKey]);

  // Sync with external isConfirmButtonLoading state
  useEffect(() => {
    if (!isConfirmButtonLoading && isConfirming) {
      setIsConfirming(false);
    }
  }, [isConfirmButtonLoading]);
    // Function to copy booking number to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      // Track the copy action
      trackUserInteraction('UI:CopyBookingNumber', { bookingNumber: text });
    });
  };
  
  // Function to format the createdAt timestamp
  const formatCreatedAt = (timestamp: string | undefined) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    } catch (error) {
      console.error('Error formatting createdAt timestamp:', error);
      return timestamp;
    }
  };
  
  // Determine title based on mode
  const title = mode === 'summary' ? 'Booking Summary' : 'Booking Confirmation';
  
  // Determine status color and message based on bookingStatus
  const getStatusInfo = () => {
    switch (bookingStatus) {
      case 'confirmed':
        return {
          bgColor: 'bg-success/10', // Lighter background for success
          borderColor: 'border-success',
          textColor: 'text-success-foreground', // Use foreground for text on lighter bg
          title: 'Confirmed:',
          message: 'Your booking has been confirmed. You will receive a confirmation notification shortly.'
        };
      case 'pending':
      default:
        return {
          bgColor: 'bg-warning/10', // Lighter background for warning
          borderColor: 'border-warning',
          textColor: 'text-warning-foreground', // Use foreground for text on lighter bg
          title: 'Pending:',
          message: 'Await confirmation. You\'ll be notified via email.'
        };
    }
  };
  
  const statusInfo = getStatusInfo();
  return (
    <>
      {/* Use the ProgressDialog component to show loading state, but only if parent isn't already showing it */}
      <ProgressDialog
        isOpen={isConfirming && !isParentSubmitting}
        message="Confirming your booking..."
        subMessage="Please wait while we process your confirmation."
      />
      <div className="card-container" data-testid="booking-summary">
        {/* Header section with title and back button */}
        <div className="p-3">
          <div className="flex items-center mb-2">
            {mode === 'summary' && (
              <button
                onClick={onBack}
                className="mr-2 p-1 hover:bg-secondary rounded-full transition-colors"
                data-testid="booking-summary-back-button"
              >
                <ArrowLeft className="h-4 w-4 text-foreground" />
              </button>
            )}
            <h2 className="text-lg font-bold" data-testid="booking-summary-title">
              {title}
              {mode === 'confirmation' && bookingStatus === 'confirmed' && (
                <CheckCircle className="inline-block ml-2 h-5 w-5 text-success" />
              )}
            </h2>
          </div>          {/* Booking ID/Number display - adjusted for confirmation mode */}
          {(bookingId || bookingNumber) && (
            <div className="mb-3 text-sm text-center" data-testid="booking-summary-booking-info">
              <span className="font-semibold">
                {bookingNumber ? 'Booking Number:' : 'Booking ID:'}
              </span>
              {' '}
              {bookingNumber ? (
                <div className="inline-flex items-center">
                  <span className="bg-info/20 font-bold text-info-foreground px-3 py-1 rounded-md border border-info" data-testid="booking-summary-booking-number">
                    {bookingNumber}
                  </span>
                  <button
                    className="ml-2 p-1.5 text-foreground-muted hover:text-foreground rounded-full hover:bg-secondary transition-colors"
                    onClick={() => copyToClipboard(bookingNumber)}
                    title="Copy booking number to clipboard"
                    data-testid="booking-summary-copy-booking-number-button"
                  >
                    {copied ? <Check className="h-5 w-5 text-success" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
              ) : (
                <span data-testid="booking-summary-booking-id">{bookingId || bookingNumber}</span>
              )}

              {/* Display booking created date/time if available */}
              {createdAt && (
                <div className="text-xs text-foreground-muted mt-1" data-testid="booking-summary-created-at">
                  Created: {formatCreatedAt(createdAt)}
                </div>
              )}
            </div>
          )}

          {/* Passenger & Contact Details Section - shown in both modes */}
          <div className="mb-4 p-3 bg-secondary rounded-lg shadow" data-testid="booking-summary-passenger-section">
            <h3 className="text-md font-semibold mb-2 text-secondary-foreground flex items-center">
              <UserCircle2 className="h-5 w-5 mr-2 text-secondary-foreground" />
              Passenger & Contact Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-secondary-foreground">
              <div>
                {passengerName && <div data-testid="booking-summary-passenger-name"><span className="font-semibold">Passenger:</span> {passengerName}</div>}
                {companyName && (
                  <div data-testid="booking-summary-company-name"><span className="font-semibold">Company:</span> {companyName}</div>
                )}
                <div data-testid="booking-summary-phone"><span className="font-semibold">Phone:</span> {countryCode} {phoneNumber}</div>
                {email && (
                  <div data-testid="booking-summary-email"><span className="font-semibold">Email:</span> {email}</div>
                )}
                {communicationPrefs && (
                  <div data-testid="booking-summary-communication-prefs"><span className="font-semibold">Communication:</span> {formatCommunicationPrefs(communicationPrefs)}</div>
                )}
              </div>
              <div>
                {passengers !== undefined && (
                  <div data-testid="booking-summary-passengers-count">
                    <p className="font-semibold">Passengers:{passengers}</p>
                  </div>
                )}
                {luggage !== undefined && (
                  <div data-testid="booking-summary-luggage-count">
                    <p className="font-semibold">Luggage:{luggage} piece(s)</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Trip Details Section - shown in both modes */}
          <div className="mb-4 p-3 bg-secondary rounded-lg shadow" data-testid="booking-summary-trip-section">
            <h3 className="text-md font-semibold mb-2 text-secondary-foreground flex items-center">
              <CarFront className="h-5 w-5 mr-2 text-secondary-foreground" />
              Trip Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-secondary-foreground">              <div>                <BookingDetails
                  pickupLocation={pickupLocation}
                  dropoffLocation={dropoffLocation}
                  estimatedDuration={estimatedDuration}
                  pickupType={pickupType}
                  pickupDate={pickupDate}
                  pickupTime={pickupTime}
                  paymentMethod={paymentMethod}
                  timezone={timezone}
                />
              </div>
              <div className="space-y-3"> {/* Adjusted spacing and added Ride Details sub-section */}
                <h4 className="font-semibold text-secondary-foreground">Ride Details:</h4>
                <VehiclePreference comfortLevel={comfortLevel} />
              </div>
            </div>
          </div>

          {/* Status info boxes - shown in both modes */}
          <div className="border-t border-border pt-2 mt-2 flex flex-col md:flex-row md:space-x-2 md:space-y-0 space-y-2" data-testid="booking-summary-status-section">
            <div className={`flex-1 p-2 ${statusInfo.bgColor} border-l-4 ${statusInfo.borderColor} rounded text-xs flex items-center`} data-testid="booking-summary-status-info-box">
              <span className={`font-semibold ${statusInfo.textColor} mr-1`}>{statusInfo.title}</span> <span className="text-foreground-muted">{statusInfo.message}</span>
            </div>
            <div className="flex-1 p-2 bg-info/10 border-l-4 border-info rounded text-xs flex items-center" data-testid="booking-summary-vehicle-notice-box">
              <span className="font-semibold text-info-foreground mr-1">Vehicle Notice:</span> <span className="text-foreground-muted">Allocated vehicle may differ; best fit will be assigned.</span>
            </div>
          </div>          {/* Action buttons section - different based on mode */}
          <div className="mt-2">
            {mode === 'summary' && <TermsAndConditions />}
            
            {mode === 'summary' ? (
              <ActionButtons
                onCancel={onCancel}                onConfirm={async () => {
                  console.log('BookingSummary: Confirm Booking button clicked.');
                  setIsConfirming(true);
                  try {
                    console.log('BookingSummary: Calling parent onConfirm handler...');
                    trackBookingEvent('BookingConfirmAttempt', { bookingId });
                    await onConfirm(); 
                    console.log('BookingSummary: Parent onConfirm handler completed successfully.');
                    trackBookingEvent('BookingConfirmSuccess', { bookingId });
                  } catch (error) {
                    console.error('BookingSummary: Exception occurred when calling parent onConfirm handler:', error);
                    // Track the error
                    trackError('Booking confirmation failed', false);
                    // Using alert as a fallback, but ideally this would be handled with a proper error UI component
                    alert('An error occurred during the confirmation process. Please try again.');
                  } finally {
                    // Only set isConfirming to false if parent isn't taking over the loading state
                    if (!isParentSubmitting) {
                      setIsConfirming(false);
                    }
                    console.log('BookingSummary: Confirmation process finished, isConfirming set to false.');
                  }
                }}
                onEdit={onBack}
                isConfirming={isConfirming || isParentSubmitting || isConfirmButtonLoading}
              />
            ) : (
              /* Confirmation mode content */
              <>
                {/* Show credentials for anonymous bookings */}
                {isAnonymousBooking && generatedPassword && email && (
                  <AnonymousBookingCredentials
                    email={email}
                    generatedPassword={generatedPassword}
                  />
                )}

                <div className="flex justify-center mt-4">
                  <button onClick={() => {
                    console.log('BookingSummary: Done button clicked');
                    console.log('BookingSummary: isAnonymousBooking:', isAnonymousBooking);

                    // For anonymous bookings, reset to new booking form
                    // For authenticated users, navigate to bookings list
                    if (isAnonymousBooking) {
                      console.log('BookingSummary: Anonymous booking - calling onDone to reset form');
                      onDone?.();
                    } else {
                      const targetPath = `/${serviceKey}/bookings`;
                      console.log('BookingSummary: Authenticated user - navigating to:', targetPath);
                      navigate(targetPath);
                    }
                  }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  data-testid="booking-summary-done-button"
                >
                  Done
                </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
